import { CountryFlag, countryName } from "@nexus/ui";
import {
  firstError,
  resolveWidgetTitle,
  MetricLabel,
  MetricValue,
  unwrap,
  Widget,
  WidgetIssueState,
  WidgetSkeleton,
  WidgetState,
} from "@nexus/widget-sdk";
import type {
  WidgetDataRequirements,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
} from "@nexus/widget-sdk";
import {
  countryAudienceLine,
  countryAudienceMetric,
  getMetricTrend,
  PIRACY_AUDIENCE_COUNTRIES_CONTRACT,
  TrendBadge,
  useWidgetMotion,
  type CountryAudienceGroupConfig,
  type MetricTrend,
  type PiracyAudienceCountriesData,
  type PiracyAudienceCountry,
} from "@nexus/widget-kit";
import { countryGroups, figureShown } from "./settings";
import { TrendLine } from "./TrendLine";

export { settings } from "./settings";

/** The tile's own title variables, filled from its data (see `definition.titleVariables`). */
export const TITLE_VARIABLES = [
  { name: "country.flag", label: "Country flag", example: "Flag" },
  { name: "country.name", label: "Country name", example: "Togo" },
] as const;

/** The default title: the country's flag, then its name. */
export const DEFAULT_TITLE = "{{country.flag}} {{country.name}}";

export interface PiracyAudienceCountryConfig {
  title?: string;
  titleSize?: "standard" | "medium" | "large";
  /** The columns: a name and the platforms each adds up (IPTV then Web by default). */
  groups?: CountryAudienceGroupConfig[];
  /** The change against the previous period, as a badge. Default on. */
  showTrend?: boolean;
  /** The average audience across the period, as a line. Default on. */
  showLine?: boolean;
  /** The period's peak, in the detail line. Default on (see `figureShown`). */
  showMaxViewers?: boolean;
  /** The estimated viewers disrupted, in the detail line. Default on (see `figureShown`). */
  showViewersDisrupted?: boolean;
  /** Earlier tiles: tile-wide peak and disruption, before columns had figures. */
  showPeak?: boolean;
  showDisrupted?: boolean;
}

export const manifest: WidgetManifest = {
  id: "piracy-audience-country",
  name: "Country audience",
  description:
    "One country's piracy audience by platform group: the average with its trend against the previous period and its line across the period, then peak and disruption.",
  version: "1.0.0",
  dataRequests: {
    country: {
      source: "piracy-audience-countries",
      contract: PIRACY_AUDIENCE_COUNTRIES_CONTRACT,
      configurable: true,
      sources: ["piracy-audience-countries"],
    },
  },
};

const GROUP_DOTS = ["bg-(--chart-1)", "bg-(--chart-4)", "bg-(--chart-2)", "bg-(--chart-3)"];
const exact = new Intl.NumberFormat("en", { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const figure = (value: number | null | undefined) => (value == null ? "—" : compact.format(value));

/**
 * The line's color tells the trend at a glance across the mosaic: a pirate
 * audience that falls is good news (green), one that rises is not (red).
 */
function trendColor(trend: MetricTrend | null): string {
  if (trend?.direction === "down") return "var(--status-ok)";
  if (trend?.direction === "up") return "var(--status-error)";
  return "var(--text-muted)";
}

/** One platform group: its average audience, trend and line, then its details. */
function Column({
  country,
  group,
  index,
  config,
  motion,
}: {
  country: PiracyAudienceCountry;
  group: CountryAudienceGroupConfig;
  index: number;
  config: PiracyAudienceCountryConfig;
  motion: boolean;
}) {
  const label = group.label.trim() || `Group ${index + 1}`;
  const average = countryAudienceMetric(country, group.id, "average-viewers");
  const peak = countryAudienceMetric(country, group.id, "max-viewers");
  const disrupted = countryAudienceMetric(country, group.id, "viewers-disrupted");
  const trend = config.showTrend !== false && average ? getMetricTrend(average) : null;
  const line = config.showLine !== false ? countryAudienceLine(country, group.id) : null;
  const measured = average?.value != null;
  const details = [
    figureShown(config, "showMaxViewers") && measured ? `Peak ${figure(peak?.value)}` : null,
    figureShown(config, "showViewersDisrupted") && disrupted?.value != null
      ? `${figure(disrupted.value)} disrupted`
      : null,
  ].filter(Boolean);
  return (
    // A narrow tile (many countries on a screen) keeps the name, trend and figure:
    // the line and the detail line need the room of a wider tile.
    <section aria-label={label} className="flex min-w-0 flex-col gap-2 @max-sm/widget:gap-0.5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="inline-flex min-w-0 items-center gap-2 text-metric-label font-semibold text-secondary">
          <span
            className={`size-2 shrink-0 rounded-full ${GROUP_DOTS[index % GROUP_DOTS.length]}`}
            aria-hidden
          />
          <span className="truncate">{label}</span>
        </span>
        {trend ? <TrendBadge trend={trend} polarity="lower-is-better" motion={motion} /> : null}
      </div>
      <div className="flex min-w-0 items-end gap-4">
        <div className="shrink-0">
          <MetricValue
            size="headline"
            title={
              measured ? `${exact.format(average!.value!)} average viewers` : "No measurements"
            }
          >
            {figure(average?.value)}
          </MetricValue>
          <MetricLabel size="compact" className="@max-sm/widget:sr-only">
            Average viewers
          </MetricLabel>
        </div>
        {line ? (
          <div className="h-[3.25rem] min-w-12 flex-1 self-center wall-short:h-10 @max-sm/widget:hidden">
            <TrendLine
              values={line}
              color={trendColor(trend)}
              label={`${label} average audience across the period: ${line
                .map((value) => (value === null ? "no data" : exact.format(value)))
                .join(", ")}`}
            />
          </div>
        ) : null}
      </div>
      {details.length || !measured ? (
        <p className="flex min-w-0 flex-wrap gap-x-3 text-metric-detail text-muted tabular-nums @max-sm/widget:hidden">
          {!measured ? <span>No measurements in the period</span> : null}
          {details.map((detail) => (
            <span key={detail}>{detail}</span>
          ))}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The piracy audience of one country, as a dashboard tile. The dashboard builds
 * the country mosaic by placing one tile per country, each choosing its country
 * with the Countries filter of its data settings; tiles with the same tenants
 * and period read one request each, for their own country only. Platform groups
 * are measured independently; their values have equal weight and wrap to the
 * available space. Sizes use rem to follow the host's presentation scale.
 */
export function Component({ data, config }: WidgetProps<PiracyAudienceCountryConfig>) {
  const error = firstError(data, ["country"]);
  const result = unwrap<PiracyAudienceCountriesData>(data.country);
  const countries = result?.countries ?? [];
  const country = countries.length === 1 ? countries[0] : null;
  const groups = countryGroups(config);
  const motion = useWidgetMotion();
  const name = country ? (countryName(country.countryCode) ?? country.countryCode) : null;
  // The title is the author's: the country's flag and name are variables of it.
  const title = resolveWidgetTitle(config.title ?? DEFAULT_TITLE, {
    "country.name": name ?? undefined,
    "country.flag": country ? (
      <CountryFlag
        country={country.countryCode}
        className="h-[0.85em] w-[1.2em] align-[-0.05em] object-cover"
      />
    ) : undefined,
  });
  return (
    <Widget
      title={
        title ? (
          <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1">
            {title}
          </span>
        ) : config.title?.trim() ? undefined : (
          manifest.name
        )
      }
      titleClassName={
        config.titleSize === "large"
          ? "text-[1.5rem]! leading-tight"
          : config.titleSize === "medium"
            ? "text-[1.25rem]! leading-tight"
            : undefined
      }
    >
      {error ? (
        <WidgetIssueState error={error} />
      ) : !result ? (
        <WidgetSkeleton shape="kpi" />
      ) : countries.length > 1 ? (
        <WidgetState
          title="Choose the country of this tile"
          description="In its data settings, set Countries to one country."
        />
      ) : !country ? (
        <WidgetState title="No measurements for this country in the period" />
      ) : (
        // Columns side by side, each its own platform group; they wrap on a narrow tile.
        <div className="density-metric-padding flex h-full min-h-0 flex-col justify-center">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,15rem),1fr))] gap-x-8 gap-y-5 @max-sm/widget:gap-y-2.5">
            {groups.map((group, index) => (
              <Column
                key={group.id}
                country={country}
                group={group}
                index={index}
                config={config}
                motion={motion}
              />
            ))}
          </div>
        </div>
      )}
    </Widget>
  );
}

export const defaultConfig: PiracyAudienceCountryConfig = {
  title: DEFAULT_TITLE,
  titleSize: "standard",
};

/**
 * The tile's country, and the comparison and line only when shown: they cost
 * the report one more query each way, so a tile without them asks for neither.
 */
export function getDataRequirements(
  config: PiracyAudienceCountryConfig = {},
): Record<string, WidgetDataRequirements> {
  return {
    country: {
      mode: "select",
      fields: [
        "countries",
        "freshness",
        ...(config.showTrend !== false ? ["trend"] : []),
        ...(config.showLine !== false ? ["history"] : []),
      ],
    },
  };
}
const widgetModule: WidgetModule<PiracyAudienceCountryConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements,
};
export default widgetModule;
